import { AdequacyRegionSetAggregate, AdequacySpecialtyGroupAggregate } from "@app/projects/results/adequacy";
import { AnalysisSpecialtyGroup, ResultRegion } from "@app/projects/results/common";
import { RegionGranularity } from "@app/region-sets";
import { RegionSetStatusType } from "../.././enumerators";
import { AggregateDisplayType, SpecialtyGroupEntityType } from "../.././literals";

interface AdequacyAggregate {
  key: string;
  defaultDisplayOrder: number;
  displayText: string;
  linkSuffix: string;

  regionCode: string;
  regionSetId: number;
  regionSetDisplayName: string;
  regionStatus?: RegionSetStatusType;
  regionSetClassification?: string;
  regionGranularity: RegionGranularity;
  subRegionCode: string;

  specialtyGroupCode?: string;
  specialtyGroupDisplayName?: string;
  specialtyGroupDisplayOrder?: number;
  specialtyGroupEntityType?: SpecialtyGroupEntityType;
  // this is ONLY used to determine whether the "include expandable specialties" 
  // checkbox shows up on the create report page
  specialtyGroupIsExpandable?: boolean;
  specialtyGroupIsInformational?: boolean;
  specialtyGroupName?: string;

  accessTotalCount: number;
  accessMetCount: number;
  accessNotMetCount: number;

  servicingTotalCount: number;
  servicingMetCount: number;
  servicingNotMetCount: number;

  exceptionCount?: number;
  approvedExceptionCount: number;
  approvedExceptionNoGapCount: number;

  accessOnlyMetCount?: number;
  servicingOnlyMetCount: number;
  noneMetCount: number;

  percentPassBoth: number;
  percentPassAccessOnly?: number;
  percentPassServicingOnly: number;
  percentPassNone: number;
  percentPassByException: number;

  adequacyIndex: number;
  adequacyIndexWithExceptions: number;
  adequacyIndexInformationalOnly: number;
  adequacyIndexWithInformational: number;

  recentlyAttestedUniqueNPICount?: number;
  recentlyAttestedUniqueNPIPercent?: number;

  fourStarsGreaterAnnually?: number;
  lessThanFourStarsAnnually?: number;
  penetration?: number;

  membershipRiskCount?: number;
  membershipRiskServiceAreaCount?: number;
  percentMembershipRisk?: number;

  fullMemberCountWithAccess?: number,
  fullMemberCountWithoutAccess?: number,

  projectedAdequacyIndex?: number;
  projectedPercentPassBoth?: number;

  gapCount: number;
  noGapCount?: number;
  unfillableGapCount?: number;
  fillableBySingleProviderCount?: number;
  partiallyFillableGapCount?: number;
  fillableByMultipleProvidersCount?: number;
  fillableCount?: number;

  parentAbbreviation?: string;
  parentDisplayName?: string;
  projectCount?: number;
  membershipRiskProjectIds?: number[];
  stateDisplayName?: string;
  percentGaps: number;
  filterPath?: string;

  membershipName?: string;
  fullMembershipCount?: number;
  isTelehealth?: string;

  accessMetCountCMSCredit?: number,
  percentPassBothCMSCredit?: number,
  percentPassAccessOnlyCMSCredit?: number,
  percentPassNoneCMSCredit?: number,
  percentPassServicingOnlyCMSCredit?: number,
  percentPassOneCMSCredit?: number,
  accessOnlyMetCountCMSCredit?: number,
  noneMetCountCMSCredit?: number,
  gapCountCMSCredit?: number,
  servicingOnlyMetCountCMSCredit?: number,
  adequacyIndexCMSCredit?: number,
  approvedExceptionCountCMSCredit?: number,
  percentPassByExceptionCMSCredit?: number,
  hideAlias?: boolean,
}

class AdequacyAggregateImpl implements AdequacyAggregate {
  public constructor(
    public key: string,
    private aggregateDisplayType: AggregateDisplayType,

    public regionCode: string,
    public regionGranularity: RegionGranularity,
    public regionSetDisplayName: string,
    public regionSetId: number,
    public subRegionCode: string,

    public accessMetCount: number,
    public accessNotMetCount: number,
    public accessTotalCount: number,

    public servicingMetCount: number,
    public servicingNotMetCount: number,
    public servicingTotalCount: number,

    public approvedExceptionCount: number,
    public approvedExceptionNoGapCount: number,

    public servicingOnlyMetCount: number,
    public noneMetCount: number,

    public percentPassByException: number,
    public percentPassBoth: number,
    public percentPassServicingOnly: number,
    public percentPassNone: number,

    public adequacyIndex: number,
    public adequacyIndexInformationalOnly: number,
    public adequacyIndexWithExceptions: number,
    public adequacyIndexWithInformational: number,

    public gapCount: number,
    public percentGaps: number,

    public fullMemberCountWithAccess?: number,
    public fullMemberCountWithoutAccess?: number,

    public regionStatus?: RegionSetStatusType,
    public regionSetClassification?: string,

    public specialtyGroupCode?: string,
    public specialtyGroupDisplayName?: string,
    public specialtyGroupDisplayOrder?: number,
    public specialtyGroupEntityType?: SpecialtyGroupEntityType,
    public specialtyGroupIsExpandable?: boolean,
    public specialtyGroupIsInformational?: boolean,
    public specialtyGroupName?: string,

    public recentlyAttestedUniqueNPICount?: number,
    public recentlyAttestedUniqueNPIPercent?: number,

    public fourStarsGreaterAnnually?: number,
    public lessThanFourStarsAnnually?: number,
    public penetration?: number,

    public membershipRiskCount?: number,
    public membershipRiskServiceAreaCount?: number,
    public percentMembershipRisk?: number,

    public projectedAdequacyIndex?: number,
    public projectedPercentPassBoth?: number,

    public percentPassAccessOnly?: number,
    public exceptionCount?: number,
    public accessOnlyMetCount?: number,
    public noGapCount?: number,
    public unfillableGapCount?: number,
    public fillableBySingleProviderCount?: number,
    public partiallyFillableGapCount?: number,
    public fillableByMultipleProvidersCount?: number,
    public fillableCount?: number,

    public parentAbbreviation?: string,
    public parentDisplayName?: string,

    public projectCount?: number,
    public stateDisplayName?: string,

    public membershipName?: string,
    public fullMembershipCount?: number,
    public isTelehealth?: string,

    public accessMetCountCMSCredit?: number,
    public percentPassBothCMSCredit?: number,
    public percentPassAccessOnlyCMSCredit?: number,
    public percentPassNoneCMSCredit?: number,
    public percentPassServicingOnlyCMSCredit?: number,
    public percentPassOneCMSCredit?: number,
    public accessOnlyMetCountCMSCredit?: number,
    public noneMetCountCMSCredit?: number,
    public gapCountCMSCredit?: number,
    public servicingOnlyMetCountCMSCredit?: number,
    public adequacyIndexCMSCredit?: number,
    public approvedExceptionCountCMSCredit?: number,
    public percentPassByExceptionCMSCredit?: number,
    public hideAlias?: boolean,
  ) { }

  public get defaultDisplayOrder(): number {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetId;
      case "specialty":
        return this.specialtyGroupDisplayOrder;
      case "unknown":
        return null;
    }
  }

  public get displayText(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return this.regionSetDisplayName;
      case "specialty":
        return this.specialtyGroupDisplayName;
      case "unknown":
        return null;
    }
  }

  public get linkSuffix(): string {
    switch (this.aggregateDisplayType) {
      case "region":
        return `/${this.regionGranularity}/${this.regionCode}`;
      case "specialty":
        return `/specialty/${encodeURIComponent(this.specialtyGroupCode)}`;
      case "unknown":
        return null;
    }
  }
}

function buildAdequacyAggregate(
  key: string,
  resultRegion: ResultRegion,
  aggregate: AdequacyRegionSetAggregate | AdequacySpecialtyGroupAggregate,
  aggregateDisplayType: AggregateDisplayType,
  specialtyGroup?: AnalysisSpecialtyGroup,
  regionStatus?: RegionSetStatusType,
  hasAnyExpandableSpecialtyGroups?: boolean,
): AdequacyAggregate {
  //TODO: Everything in this function should be handled in the backend.
  //We shouldn't be taking a set of objects and building a new object.
  //We should just return the object we want from the API.
  if (!resultRegion || !aggregate) {
    return {} as AdequacyAggregate;
  }
  if (isSpecialtyGroupAggregate(aggregate)) {
    //A specialty group is required and only used when passing in an aggregate of this type
    //we're not checking if it (specialty group) exists bc we want it to fail if it doesn't exist
    return new AdequacyAggregateImpl(
      key,
      aggregateDisplayType,

      resultRegion.regionCode,
      resultRegion.regionGranularity,
      resultRegion.displayName,
      resultRegion.regionSetId,
      resultRegion.subRegionCode,

      aggregate.accessMetCount,
      aggregate.accessNotMetCount,
      aggregate.accessTotalCount,

      aggregate.servicingMetCount,
      aggregate.servicingNotMetCount,
      aggregate.servicingTotalCount,

      aggregate.approvedExceptionCount || 0,
      aggregate.approvedExceptionNoGapCount || 0,

      aggregate.servicingOnlyMetCount,
      aggregate.noneMetCount,

      aggregate.percentPassByException,
      aggregate.percentPassBoth,
      aggregate.percentPassServicingOnly,
      aggregate.percentPassNone,

      aggregate.adequacyIndex,
      aggregate.adequacyIndexInformationalOnly,
      aggregate.adequacyIndexWithExceptions,
      aggregate.adequacyIndexWithInformational,

      aggregate.gapCount,
      aggregate.percentGaps,

      aggregate.fullMemberCountWithAccess,
      aggregate.fullMemberCountWithoutAccess,

      regionStatus,
      aggregate.regionSetClassification,

      specialtyGroup.code,
      `${specialtyGroup.code} - ${specialtyGroup.name}`,
      specialtyGroup.displayOrder,
      specialtyGroup.entityType,
      specialtyGroup.isExpandable,
      specialtyGroup.isInformational,
      specialtyGroup.name,

      aggregate.recentlyAttestedUniqueNPICount,
      aggregate.recentlyAttestedUniqueNPIPercent,

      undefined,
      undefined,
      undefined,

      undefined,
      undefined,
      undefined,

      aggregate.projectedAdequacyIndex,
      aggregate.projectedPercentPassBoth,

      aggregate.percentPassAccessOnly || 0,
      aggregate.exceptionCount,
      aggregate.accessOnlyMetCount,
      aggregate.noGapCount,
      aggregate.unfillableGapCount,
      aggregate.fillableBySingleProviderCount,
      aggregate.partiallyFillableGapCount,
      aggregate.fillableByMultipleProvidersCount,
      aggregate.fillableCount,

      undefined,
      undefined,
      undefined,
      undefined,

      aggregate.membershipName,
      aggregate.fullMembershipCount,
      aggregate.isTelehealth,

      aggregate.accessMetCountCMSCredit,
      aggregate.percentPassBothCMSCredit,
      aggregate.percentPassAccessOnlyCMSCredit,
      aggregate.percentPassNoneCMSCredit,
      aggregate.percentPassServicingOnlyCMSCredit,
      aggregate.percentPassOneCMSCredit,
      aggregate.accessOnlyMetCountCMSCredit,
      aggregate.noneMetCountCMSCredit,
      aggregate.gapCountCMSCredit,
      aggregate.servicingOnlyMetCountCMSCredit,
      aggregate.adequacyIndexCMSCredit,
      aggregate.approvedExceptionCountCMSCredit,
      aggregate.percentPassByExceptionCMSCredit,
      aggregate.hideAlias,
    );

  } else {
    return new AdequacyAggregateImpl(
      key,
      aggregateDisplayType,

      resultRegion.regionCode,
      resultRegion.regionGranularity,
      resultRegion.displayName,
      resultRegion.regionSetId,
      resultRegion.subRegionCode,

      aggregate.accessMetCount,
      aggregate.accessNotMetCount,
      aggregate.accessTotalCount,

      aggregate.servicingMetCount,
      aggregate.servicingNotMetCount,
      aggregate.servicingTotalCount,

      aggregate.approvedExceptionCount || 0,
      aggregate.approvedExceptionNoGapCount || 0,

      aggregate.servicingOnlyMetCount,
      aggregate.noneMetCount,

      aggregate.percentPassByException,
      aggregate.percentPassBoth,
      aggregate.percentPassServicingOnly,
      aggregate.percentPassNone,

      aggregate.adequacyIndex,
      aggregate.adequacyIndexInformationalOnly,
      aggregate.adequacyIndexWithExceptions,
      aggregate.adequacyIndexWithInformational,

      aggregate.gapCount,
      aggregate.percentGaps,

      aggregate.fullMemberCountWithAccess,
      aggregate.fullMemberCountWithoutAccess,

      regionStatus,
      aggregate.regionSetClassification,

      undefined,
      undefined,
      undefined,

      undefined,
      hasAnyExpandableSpecialtyGroups,
      undefined,
      undefined,

      aggregate.recentlyAttestedUniqueNPICount,
      aggregate.recentlyAttestedUniqueNPIPercent,

      aggregate.fourStarsGreaterAnnually,
      aggregate.lessThanFourStarsAnnually,
      aggregate.penetration,

      aggregate.membershipRiskCount,
      aggregate.membershipRiskServiceAreaCount,
      aggregate.percentMembershipRisk,

      aggregate.projectedAdequacyIndex,
      aggregate.projectedPercentPassBoth,

      aggregate.percentPassAccessOnly || 0,
      aggregate.exceptionCount,
      aggregate.accessOnlyMetCount,
      aggregate.noGapCount,
      aggregate.unfillableGapCount,
      aggregate.fillableBySingleProviderCount,
      aggregate.partiallyFillableGapCount,
      aggregate.fillableByMultipleProvidersCount,
      aggregate.fillableCount,

      undefined,
      undefined,
      undefined,
      undefined,

      aggregate.membershipName,
      aggregate.fullMembershipCount,
      undefined,

      aggregate.accessMetCountCMSCredit,
      aggregate.percentPassBothCMSCredit,
      aggregate.percentPassAccessOnlyCMSCredit,
      aggregate.percentPassNoneCMSCredit,
      aggregate.percentPassServicingOnlyCMSCredit,
      aggregate.percentPassOneCMSCredit,
      aggregate.accessOnlyMetCountCMSCredit,
      aggregate.noneMetCountCMSCredit,
      aggregate.gapCountCMSCredit,
      aggregate.servicingOnlyMetCountCMSCredit,
      aggregate.adequacyIndexCMSCredit,
      aggregate.approvedExceptionCountCMSCredit,
      aggregate.percentPassByExceptionCMSCredit,
      aggregate.hideAlias,
    );
  }

}

/**
 * Clones an `AdequacyAggregate` while setting a new displayText property based on the `AdequacyAggregateDisplayType`
 * It DOES actually create a new object
 * @param adequacyAggregate
 * @param aggregateDisplayType
 */
function psuedoClone(
  adequacyAggregate: AdequacyAggregate,
  aggregateDisplayType: AggregateDisplayType,
): AdequacyAggregate {

  return new AdequacyAggregateImpl(
    adequacyAggregate.key,
    aggregateDisplayType,

    adequacyAggregate.regionCode,
    adequacyAggregate.regionGranularity,
    adequacyAggregate.regionSetDisplayName,
    adequacyAggregate.regionSetId,
    adequacyAggregate.subRegionCode,

    adequacyAggregate.accessMetCount,
    adequacyAggregate.accessNotMetCount,
    adequacyAggregate.accessTotalCount,

    adequacyAggregate.servicingMetCount,
    adequacyAggregate.servicingNotMetCount,
    adequacyAggregate.servicingTotalCount,

    adequacyAggregate.approvedExceptionCount,
    adequacyAggregate.approvedExceptionNoGapCount,

    adequacyAggregate.servicingOnlyMetCount,
    adequacyAggregate.noneMetCount,

    adequacyAggregate.percentPassByException,
    adequacyAggregate.percentPassBoth,
    adequacyAggregate.percentPassServicingOnly,
    adequacyAggregate.percentPassNone,

    adequacyAggregate.adequacyIndex,
    adequacyAggregate.adequacyIndexInformationalOnly,
    adequacyAggregate.adequacyIndexWithExceptions,
    adequacyAggregate.adequacyIndexWithInformational,

    adequacyAggregate.gapCount,
    adequacyAggregate.percentGaps,

    adequacyAggregate.fullMemberCountWithAccess,
    adequacyAggregate.fullMemberCountWithoutAccess,

    adequacyAggregate.regionStatus,
    adequacyAggregate.regionSetClassification,

    adequacyAggregate.specialtyGroupCode,
    adequacyAggregate.specialtyGroupDisplayName,
    adequacyAggregate.specialtyGroupDisplayOrder,
    adequacyAggregate.specialtyGroupEntityType,
    adequacyAggregate.specialtyGroupIsExpandable,
    adequacyAggregate.specialtyGroupIsInformational,
    adequacyAggregate.specialtyGroupName,

    adequacyAggregate.recentlyAttestedUniqueNPICount,
    adequacyAggregate.recentlyAttestedUniqueNPIPercent,

    adequacyAggregate.fourStarsGreaterAnnually,
    adequacyAggregate.lessThanFourStarsAnnually,
    adequacyAggregate.penetration,

    adequacyAggregate.membershipRiskCount,
    adequacyAggregate.membershipRiskServiceAreaCount,
    adequacyAggregate.percentMembershipRisk,

    adequacyAggregate.projectedAdequacyIndex,
    adequacyAggregate.projectedPercentPassBoth,

    adequacyAggregate.percentPassAccessOnly || 0,
    adequacyAggregate.exceptionCount,
    adequacyAggregate.accessOnlyMetCount,
    adequacyAggregate.noGapCount,
    adequacyAggregate.unfillableGapCount,
    adequacyAggregate.fillableBySingleProviderCount,
    adequacyAggregate.partiallyFillableGapCount,
    adequacyAggregate.fillableByMultipleProvidersCount,
    adequacyAggregate.fillableCount,

    adequacyAggregate.parentAbbreviation,
    adequacyAggregate.parentDisplayName,

    adequacyAggregate.projectCount,
    adequacyAggregate.stateDisplayName,

    adequacyAggregate.membershipName,
    adequacyAggregate.fullMembershipCount,
    adequacyAggregate.isTelehealth,

    adequacyAggregate.accessMetCountCMSCredit,
    adequacyAggregate.percentPassBothCMSCredit,
    adequacyAggregate.percentPassAccessOnlyCMSCredit,
    adequacyAggregate.percentPassNoneCMSCredit,
    adequacyAggregate.percentPassServicingOnlyCMSCredit,
    adequacyAggregate.percentPassOneCMSCredit,
    adequacyAggregate.accessOnlyMetCountCMSCredit,
    adequacyAggregate.noneMetCountCMSCredit,
    adequacyAggregate.gapCountCMSCredit,
    adequacyAggregate.servicingOnlyMetCountCMSCredit,
    adequacyAggregate.adequacyIndexCMSCredit,
    adequacyAggregate.approvedExceptionCountCMSCredit,
    adequacyAggregate.percentPassByExceptionCMSCredit,
    adequacyAggregate.hideAlias,
  );

}

function isSpecialtyGroupAggregate(aggregate: AdequacyRegionSetAggregate | AdequacySpecialtyGroupAggregate): aggregate is AdequacySpecialtyGroupAggregate {
  return (aggregate as AdequacySpecialtyGroupAggregate).specialtyGroupCode !== undefined;
}

export { AdequacyAggregate, buildAdequacyAggregate, psuedoClone };
